import React, { useState } from "react";
import { Box, Card, Grid, Tab, Tabs, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import Ball from "./Ball";
import { elevatedCard, elevatedRow } from "./cardStyle";

const EmptyState = ({ icon, gradient, title, subtitle }) => (
  <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: gradient,
        boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
        mb: 2,
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "15px" }}>
      {title}
    </Typography>
    <Typography sx={{ color: "#ffffff88", fontSize: "13px", mt: 0.6, maxWidth: 260, mx: "auto" }}>
      {subtitle}
    </Typography>
  </Box>
);

const statusChip = (t, theme) => {
  const map = {
    WON: { label: `WON${t?.prizeSc ? ` ${t.prizeSc} SC` : ""}`, color: theme.palette.custom.green01, bg: theme.palette.custom.greenhalf },
    LOST: { label: "LOST", color: theme.palette.custom.red, bg: "#fa114f22" },
    PENDING: { label: "PENDING", color: theme.palette.custom.yellow, bg: "#DB9F0622" },
  };
  const s = map[t?.status] || map.PENDING;
  return (
    <Box
      sx={{
        background: s.bg,
        color: s.color,
        borderRadius: "8px",
        px: 1.5,
        py: 0.4,
        fontSize: "12px",
        fontWeight: 700,
      }}
    >
      {s.label}
    </Box>
  );
};

const TicketRow = ({ t, maxNumber, resultsByLotteryId, theme }) => {
  const drawResult = resultsByLotteryId?.[t?.lotteryId];
  const showResult = t?.status !== "PENDING" && drawResult?.drawnNumbers?.length;
  return (
    <Box
      sx={{
        background: "#ffffff08",
        border: "1px solid #ffffff14",
        borderRadius: "12px",
        p: 1.8,
        ...elevatedRow,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ color: "#ffffff88", fontSize: "11px", mb: 0.8 }}>
            {t?.lotteryName} · #{t?.id}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.8 }}>
            {t?.numbers?.map((n) => (
              <Ball key={n} num={n} max={maxNumber} size={32} dim={t?.status === "LOST"} />
            ))}
          </Box>
        </Box>
        {statusChip(t, theme)}
      </Box>
      {showResult && (
        <Box
          sx={{
            mt: 1.4,
            pt: 1.4,
            borderTop: "1px dashed #ffffff1e",
            display: "flex",
            alignItems: "center",
            gap: 0.8,
          }}
        >
          <Typography sx={{ color: "#ffffff77", fontSize: "10px", mr: 0.4 }}>
            Winning numbers:
          </Typography>
          {drawResult.drawnNumbers.map((n) => (
            <Ball key={n} num={n} max={maxNumber} size={26} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const TicketsPanel = ({ md, pendingTickets, pastTickets, resultsByLotteryId, maxNumber }) => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  return (
    <Grid item xs={12} md={md}>
      <Card
        sx={{
          background: theme.palette.custom.dark_d,
          border: `1px solid ${theme.palette.custom.lgray}`,
          borderRadius: "16px",
          p: { xs: 2.5, md: 3 },
          ...elevatedCard,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              color: theme.palette.custom.white_gray,
              textTransform: "none",
              fontWeight: 700,
              minHeight: 36,
            },
            "& .Mui-selected": { color: `${theme.palette.custom.green01} !important` },
            "& .MuiTabs-indicator": { background: theme.palette.custom.green01 },
          }}
        >
          <Tab label="Pending Tickets" />
          <Tab label="Past Tickets" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.4 }}>
            {pendingTickets?.length ? (
              pendingTickets.map((t) => (
                <TicketRow key={t?.id} t={t} maxNumber={maxNumber} resultsByLotteryId={resultsByLotteryId} theme={theme} />
              ))
            ) : (
              <EmptyState
                icon={<ConfirmationNumberIcon sx={{ fontSize: 26, color: "#fff" }} />}
                gradient="linear-gradient(135deg, #03B349, #016A2B)"
                title="No tickets in play yet"
                subtitle="Pick your lucky numbers above and take your shot at the jackpot"
              />
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.4 }}>
            {pastTickets?.length ? (
              pastTickets.map((t) => (
                <TicketRow key={t?.id} t={t} maxNumber={maxNumber} resultsByLotteryId={resultsByLotteryId} theme={theme} />
              ))
            ) : (
              <EmptyState
                icon={<HistoryToggleOffIcon sx={{ fontSize: 26, color: "#fff" }} />}
                gradient="linear-gradient(135deg, #DB9F06, #8a6404)"
                title="Nothing here yet"
                subtitle="Once one of your draws completes, the result will show up right here."
              />
            )}
          </Box>
        )}
      </Card>
    </Grid>
  );
};

export default TicketsPanel;
